import React, { useEffect, useState } from 'react';
import { apiClient } from '../services/apiClient';

interface AuthenticatedImageProps {
  src?: string | null;
  apiPath?: string;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
}

const isDirectSrc = (value?: string | null): value is string =>
  !!value && (value.startsWith('data:') || value.startsWith('blob:') || value.startsWith('http://') || value.startsWith('https://'));

export const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  apiPath,
  alt,
  className,
  fallback = null,
}) => {
  const [objectUrl, setObjectUrl] = useState('');

  useEffect(() => {
    let createdUrl = '';
    let cancelled = false;

    const load = async () => {
      if (isDirectSrc(src)) {
        setObjectUrl(src);
        return;
      }

      if (!apiPath) {
        setObjectUrl('');
        return;
      }

      try {
        const urlWithCacheBuster = apiPath.includes('?') ? `${apiPath}&t=${Date.now()}` : `${apiPath}?t=${Date.now()}`;
        const response = await apiClient.get(urlWithCacheBuster, { responseType: 'blob' });
        if (cancelled) return;
        createdUrl = URL.createObjectURL(response.data);
        setObjectUrl(createdUrl);
      } catch {
        if (!cancelled) {
          setObjectUrl('');
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [src, apiPath]);

  if (!objectUrl) {
    return <>{fallback}</>;
  }

  return <img src={objectUrl} alt={alt} className={className} />;
};
